import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, Upload, FileText, Sparkles, Loader2, Check, Copy,
  Target, Briefcase, MapPin, X, AlertTriangle, CheckCircle2,
  XCircle, Download, ClipboardList, ChevronDown, ChevronUp, Edit3, Eye, Award,
  Mail, Linkedin, MessageSquare, Save, Library, Building2, User2, Rocket, Code2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE"; // cyan
const ACCENT_DEEP = "#0891B2";

function authHeaders() {
  try {
    const raw = sessionStorage.getItem("user_data");
    if (!raw) return {};
    const d = JSON.parse(raw);
    return d?.session_token ? { Authorization: `Bearer ${d.session_token}` } : {};
  } catch { return {}; }
}
function isLoggedIn() {
  try { return !!JSON.parse(sessionStorage.getItem("user_data") || "{}").session_token; }
  catch { return false; }
}

function CopyBtn({ text, label = "Copy", testid }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button onClick={copy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors"
            data-testid={testid}>
      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : label}
    </button>
  );
}

function ScoreDial({ score, band }) {
  const color =
    score >= 85 ? "#22D3EE" :
    score >= 70 ? "#86EFAC" :
    score >= 55 ? "#FCD34D" :
    "#FB7185";
  const dash = (score / 100) * 282.7;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: 140, height: 140 }} data-testid="score-dial">
      <svg width="140" height="140" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r="45" stroke="#27272a" strokeWidth="10" fill="none" />
        <circle cx="55" cy="55" r="45" stroke={color} strokeWidth="10" fill="none"
                strokeDasharray={`${dash} 282.7`} strokeDashoffset="0"
                transform="rotate(-90 55 55)" strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white" data-testid="score-value">{score}</span>
        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color }} data-testid="score-band">{band}</span>
      </div>
    </div>
  );
}

const SEV_COLOR = {
  Critical: "bg-red-500/15 border-red-500/40 text-red-300",
  High: "bg-orange-500/15 border-orange-500/40 text-orange-300",
  Medium: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  Low: "bg-zinc-800 border-zinc-700 text-zinc-300",
};

export default function ResumeAtsPage() {
  // Input stage
  const [stage, setStage] = useState("input"); // input | outputs
  const [resumeText, setResumeText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [geography, setGeography] = useState("india");
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const fileInputRef = useRef(null);

  // Pipeline
  const [scoring, setScoring] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [scoreErr, setScoreErr] = useState("");

  const [activeOutput, setActiveOutput] = useState("score"); // score | rewrite | cover | linkedin | dms
  const [rewrite, setRewrite] = useState(null);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteErr, setRewriteErr] = useState("");
  const [editingRewrite, setEditingRewrite] = useState(false);
  const [editBuffer, setEditBuffer] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // Phase 2 — Cover Letter
  const [coverCompany, setCoverCompany] = useState("");
  const [coverRole, setCoverRole] = useState("");
  const [coverManager, setCoverManager] = useState("");
  const [cover, setCover] = useState(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverErr, setCoverErr] = useState("");

  // Phase 2 — LinkedIn
  const [liHeadline, setLiHeadline] = useState("");
  const [liAbout, setLiAbout] = useState("");
  const [liExperience, setLiExperience] = useState("");
  const [liSkills, setLiSkills] = useState("");
  const [linkedin, setLinkedin] = useState(null);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [linkedinErr, setLinkedinErr] = useState("");

  // Phase 2 — Recruiter DMs
  const [dmCompany, setDmCompany] = useState("");
  const [dmRole, setDmRole] = useState("");
  const [dmRecruiter, setDmRecruiter] = useState("");
  const [dmMutual, setDmMutual] = useState("");
  const [dms, setDms] = useState(null);
  const [dmsLoading, setDmsLoading] = useState(false);
  const [dmsErr, setDmsErr] = useState("");

  // Phase 2 — Library save
  const [savedPackageId, setSavedPackageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Phase 3 — Skill-gap → Python Lab recommendations
  const [skillGapRecs, setSkillGapRecs] = useState(null);
  const [skillGapLoading, setSkillGapLoading] = useState(false);

  // When score lands and JD match is present, fetch Python Lab cross-sell recs.
  useEffect(() => {
    const jd = scoreData?.semantic?.jd_match;
    if (!jd) { setSkillGapRecs(null); return; }
    const missing = jd.missing_keywords || [];
    const gaps    = jd.skill_gaps || [];
    if (missing.length + gaps.length === 0) { setSkillGapRecs(null); return; }
    setSkillGapLoading(true);
    axios.post(`${API}/resume-ats/skill-gap-recommend`, {
      missing_keywords: missing,
      skill_gaps: gaps,
      target_role: targetRole || scoreData?.semantic?.detected_role || null,
      limit: 3,
    }, { timeout: 20_000 })
      .then(r => setSkillGapRecs(r.data))
      .catch(() => setSkillGapRecs(null))
      .finally(() => setSkillGapLoading(false));
  }, [scoreData, targetRole]);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/resume-ats/parse`, fd, { timeout: 30_000 });
      setResumeText(res.data.extracted_text);
      setUploadInfo({ filename: res.data.filename, words: res.data.word_count });
    } catch (err) {
      setScoreErr(err?.response?.data?.detail || "Could not parse file. Try pasting the text instead.");
      setUploadInfo(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function runScore() {
    if (resumeText.trim().length < 50) { setScoreErr("Add at least 50 characters of resume content."); return; }
    setScoring(true); setScoreErr(""); setScoreData(null); setRewrite(null); setEditingRewrite(false);
    setCover(null); setLinkedin(null); setDms(null);
    setSavedPackageId(null); setSaveMsg("");
    try {
      const res = await axios.post(`${API}/resume-ats/score`, {
        resume_text: resumeText,
        job_description: jobDescription || null,
        target_role: targetRole || null,
        target_geography: geography || null,
      }, { timeout: 90_000 });
      setScoreData(res.data);
      // Pre-fill cover-letter / DM company-role from detected info if available
      if (!coverRole) setCoverRole(targetRole || res.data?.semantic?.detected_role || "");
      if (!dmRole) setDmRole(targetRole || res.data?.semantic?.detected_role || "");
      setStage("outputs");
      setActiveOutput("score");
    } catch (err) {
      setScoreErr(err?.response?.data?.detail || err.message || "ATS scoring failed.");
    } finally { setScoring(false); }
  }

  async function loadRewrite() {
    if (rewrite || rewriteLoading) return;
    setRewriteLoading(true); setRewriteErr("");
    try {
      const res = await axios.post(`${API}/resume-ats/rewrite`, {
        resume_text: resumeText,
        job_description: jobDescription || null,
        target_role: targetRole || null,
        target_geography: geography || null,
      }, { timeout: 90_000 });
      setRewrite(res.data);
    } catch (err) {
      setRewriteErr(err?.response?.data?.detail || "Rewrite failed.");
    } finally { setRewriteLoading(false); }
  }

  async function generateCover() {
    if (!jobDescription || jobDescription.trim().length < 30) {
      setCoverErr("Paste a job description (≥30 chars) on the input step to generate a tailored cover letter.");
      return;
    }
    setCoverLoading(true); setCoverErr(""); setCover(null);
    try {
      const res = await axios.post(`${API}/resume-ats/cover-letter`, {
        resume_text: resumeText,
        job_description: jobDescription,
        company_name: coverCompany || null,
        role_title: coverRole || null,
        hiring_manager_name: coverManager || null,
        target_geography: geography || null,
      }, { timeout: 90_000 });
      setCover(res.data);
    } catch (err) {
      setCoverErr(err?.response?.data?.detail || "Cover letter generation failed.");
    } finally { setCoverLoading(false); }
  }

  async function generateLinkedIn() {
    if (liHeadline.trim().length < 5 || liAbout.trim().length < 20 ||
        liExperience.trim().length < 20 || liSkills.trim().length < 5) {
      setLinkedinErr("Fill in all 4 LinkedIn sections (headline ≥5, about ≥20, experience ≥20, skills ≥5 chars).");
      return;
    }
    setLinkedinLoading(true); setLinkedinErr(""); setLinkedin(null);
    try {
      const res = await axios.post(`${API}/resume-ats/linkedin-optimize`, {
        headline: liHeadline,
        about: liAbout,
        experience_sample: liExperience,
        skills: liSkills,
        target_role: targetRole || null,
        target_geography: geography || null,
      }, { timeout: 90_000 });
      setLinkedin(res.data);
    } catch (err) {
      setLinkedinErr(err?.response?.data?.detail || "LinkedIn optimization failed.");
    } finally { setLinkedinLoading(false); }
  }

  async function generateDms() {
    setDmsLoading(true); setDmsErr(""); setDms(null);
    try {
      const res = await axios.post(`${API}/resume-ats/recruiter-dms`, {
        resume_text: resumeText,
        job_description: jobDescription || null,
        company_name: dmCompany || null,
        role_title: dmRole || null,
        recruiter_name: dmRecruiter || null,
        mutual_connection: dmMutual || null,
        target_geography: geography || null,
      }, { timeout: 90_000 });
      setDms(res.data);
    } catch (err) {
      setDmsErr(err?.response?.data?.detail || "Recruiter DM generation failed.");
    } finally { setDmsLoading(false); }
  }

  async function saveToLibrary() {
    if (!isLoggedIn()) {
      setSaveMsg("Login required to save application packages.");
      setTimeout(() => setSaveMsg(""), 5000);
      return;
    }
    setSaving(true); setSaveMsg("");
    try {
      const title = (coverCompany && coverRole) ? `${coverCompany} — ${coverRole}` :
                    (dmCompany && dmRole) ? `${dmCompany} — ${dmRole}` :
                    (scoreData?.semantic?.detected_role) ? scoreData.semantic.detected_role :
                    "Untitled application";
      const res = await axios.post(`${API}/resume-ats/library/save`, {
        package_id: savedPackageId || null,
        title,
        company: coverCompany || dmCompany || null,
        role: coverRole || dmRole || targetRole || scoreData?.semantic?.detected_role || null,
        resume_text: resumeText,
        job_description: jobDescription || null,
        target_role: targetRole || null,
        target_geography: geography || null,
        score: scoreData || null,
        rewritten_markdown: rewrite?.rewritten_markdown || null,
        cover_letter_markdown: cover?.cover_letter_markdown || null,
        linkedin: linkedin || null,
        recruiter_dms: dms || null,
      }, { headers: authHeaders() });
      setSavedPackageId(res.data.package_id);
      setSaveMsg(res.data.status === "created" ? "Saved ✓" : "Updated ✓");
      setTimeout(() => setSaveMsg(""), 4000);
    } catch (err) {
      const status = err?.response?.status;
      setSaveMsg(status === 401 ? "Login required to save." : (err?.response?.data?.detail || "Save failed."));
      setTimeout(() => setSaveMsg(""), 5000);
    } finally { setSaving(false); }
  }

  useEffect(() => {
    if (activeOutput === "rewrite") loadRewrite();
    // eslint-disable-next-line
  }, [activeOutput]);

  function startEdit() { setEditBuffer(rewrite.rewritten_markdown); setEditingRewrite(true); }
  function cancelEdit() { setEditingRewrite(false); setEditBuffer(""); }
  function applyEdit() {
    setRewrite(prev => ({ ...prev, rewritten_markdown: editBuffer, word_count: editBuffer.split(/\s+/).filter(Boolean).length }));
    setEditingRewrite(false);
  }

  async function downloadPdf() {
    if (!rewrite) return;
    setPdfLoading(true);
    try {
      // Best-effort: extract candidate name from first H1
      const firstH1 = rewrite.rewritten_markdown.match(/^#\s+(.+)$/m);
      const name = (firstH1 && firstH1[1]) || (scoreData?.semantic?.detected_role) || "Candidate";
      const res = await axios.post(`${API}/resume-ats/pdf`, {
        markdown: rewrite.rewritten_markdown,
        candidate_name: name,
      }, { responseType: "blob", timeout: 30_000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = name.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 50);
      a.download = `${safe}-shansoresume.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { setRewriteErr("PDF download failed."); }
    finally { setPdfLoading(false); }
  }

  function resetAll() {
    setStage("input"); setResumeText(""); setJobDescription(""); setTargetRole("");
    setUploadInfo(null); setScoreData(null); setRewrite(null); setEditingRewrite(false);
    setScoreErr(""); setRewriteErr(""); setActiveOutput("score");
    setCover(null); setCoverErr(""); setCoverCompany(""); setCoverRole(""); setCoverManager("");
    setLinkedin(null); setLinkedinErr("");
    setLiHeadline(""); setLiAbout(""); setLiExperience(""); setLiSkills("");
    setDms(null); setDmsErr(""); setDmCompany(""); setDmRole(""); setDmRecruiter(""); setDmMutual("");
    setSavedPackageId(null); setSaveMsg("");
  }

  const TABS = [
    { id: "score",    label: "ATS Score & Flags",  icon: Target },
    { id: "rewrite",  label: "ATS-Safe Rewrite",   icon: Sparkles },
    { id: "cover",    label: "Cover Letter",       icon: Mail },
    { id: "linkedin", label: "LinkedIn Optimizer", icon: Linkedin },
    { id: "dms",      label: "Recruiter DMs",      icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/ai-tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-tools">
            <ArrowLeft size={16} /> AI Tools
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/ai-tools/resume-ats/library"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-medium border border-zinc-800"
                  data-testid="library-link">
              <Library size={13} /> My Applications
            </Link>
            <div className="flex items-center gap-2" style={{ color: ACCENT }}>
              <Award size={18} />
              <span className="font-semibold tracking-tight text-sm">ShansoResume</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {stage === "input" && (
          <section className="space-y-8" data-testid="input-stage">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Beat the ATS. Land more interviews.</h1>
              <p className="text-zinc-400 text-sm mt-3 max-w-2xl">
                Upload your resume, optionally paste a job description, and get an instant ATS score plus an honest, recruiter-grade rewrite — in under a minute.
              </p>
            </div>

            {/* Upload + paste */}
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold">
                  <Upload size={15} /> 1. Your resume
                </div>
                <div className="border-2 border-dashed border-zinc-800 hover:border-cyan-500/40 rounded-xl p-5 text-center transition-colors cursor-pointer"
                     onClick={() => fileInputRef.current?.click()}
                     data-testid="upload-zone">
                  <Upload size={28} className="mx-auto text-zinc-600 mb-2" />
                  <p className="text-zinc-300 text-sm font-medium">Drop a PDF or DOCX, or click to browse</p>
                  <p className="text-zinc-500 text-[11px] mt-1">.pdf · .docx · .txt — up to 8MB</p>
                  <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onFile} data-testid="file-input" />
                </div>
                {uploading && <p className="text-zinc-500 text-xs flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Parsing your resume…</p>}
                {uploadInfo && (
                  <p className="text-emerald-300 text-xs flex items-center gap-2" data-testid="upload-success">
                    <CheckCircle2 size={13} /> Parsed: {uploadInfo.filename} · {uploadInfo.words} words
                  </p>
                )}
                <div className="text-zinc-500 text-[11px] uppercase tracking-wider">Or paste below</div>
                <textarea value={resumeText} onChange={(e) => setResumeText(e.target.value)}
                          rows={10}
                          placeholder="Paste your full resume text here…"
                          className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none font-mono"
                          data-testid="resume-textarea" />
                <p className="text-zinc-500 text-[11px]">{resumeText.length} chars · {resumeText.split(/\s+/).filter(Boolean).length} words</p>
              </div>

              <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold">
                  <Briefcase size={15} /> 2. Target role (optional, recommended)
                </div>
                <input type="text" value={targetRole} onChange={(e) => setTargetRole(e.target.value)}
                       placeholder="e.g. Senior Backend Engineer"
                       className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none"
                       data-testid="target-role-input" />
                <div>
                  <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><MapPin size={11} /> Geography</p>
                  <div className="flex gap-2">
                    {[{id:"india",label:"India"},{id:"global",label:"Global"}].map(g => (
                      <button key={g.id} onClick={() => setGeography(g.id)}
                              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${geography===g.id?"bg-cyan-500/15 border-cyan-500/50 text-cyan-300":"bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white"}`}
                              data-testid={`geo-${g.id}`}>{g.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Target size={11} /> Job description</p>
                  <textarea value={jobDescription} onChange={(e) => setJobDescription(e.target.value)}
                            rows={8}
                            placeholder="Paste the JD here to unlock keyword-gap analysis and callback probability (optional)…"
                            className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                            data-testid="jd-textarea" />
                  <p className="text-zinc-500 text-[11px] mt-1">{jobDescription.length} chars</p>
                </div>
              </div>
            </div>

            {scoreErr && <p className="text-amber-400 text-sm" data-testid="score-error">{scoreErr}</p>}
            <div className="flex items-center gap-3">
              <button onClick={runScore} disabled={scoring || resumeText.trim().length < 50}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-50"
                      style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                      data-testid="score-btn">
                {scoring ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                Run ATS analysis
              </button>
              <p className="text-zinc-500 text-xs">~15-20 seconds</p>
            </div>
          </section>
        )}

        {stage === "outputs" && scoreData && (
          <section className="space-y-5" data-testid="outputs-stage">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-zinc-500 text-[11px] uppercase tracking-wider">ATS analysis</p>
                <h2 className="text-white text-xl font-bold" data-testid="detected-role">
                  {scoreData.semantic?.detected_role || "Resume analysis"}
                  {scoreData.semantic?.detected_seniority && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 uppercase tracking-wider align-middle">{scoreData.semantic.detected_seniority}</span>
                  )}
                </h2>
                <p className="text-zinc-500 text-xs mt-0.5">Generated {new Date(scoreData.scored_at).toLocaleTimeString()}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {saveMsg && <span className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-md" data-testid="save-message">{saveMsg}</span>}
                <button onClick={saveToLibrary} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-black font-semibold text-xs disabled:opacity-50"
                        style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                        data-testid="save-to-library-btn"
                        title={isLoggedIn() ? (savedPackageId ? "Update saved application" : "Save as application package") : "Login required"}>
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {savedPackageId ? "Update" : "Save"}
                </button>
                <button onClick={resetAll}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-medium border border-zinc-800"
                        data-testid="reset-btn">
                  <X size={13} /> Start over
                </button>
              </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 border-b border-zinc-800/60" data-testid="output-tabs">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = activeOutput === t.id;
                return (
                  <button key={t.id} onClick={() => setActiveOutput(t.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${active ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
                          data-testid={`tab-${t.id}`}>
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>

            {activeOutput === "score" && (
              <div className="space-y-5" data-testid="output-score">
                {/* Overall + headline */}
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800/60 rounded-2xl p-5">
                  <div className="flex items-center gap-6 flex-wrap">
                    <ScoreDial score={scoreData.overall_score} band={scoreData.band} />
                    <div className="flex-1 min-w-[240px] space-y-2">
                      {scoreData.semantic?.top_strengths?.map((s, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-zinc-300"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> {s}</div>
                      ))}
                      {scoreData.semantic?.suggested_titles?.length > 0 && (
                        <div className="pt-2">
                          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-1">Also a match for</p>
                          <div className="flex flex-wrap gap-1.5">
                            {scoreData.semantic.suggested_titles.map((t, i) => (
                              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300" data-testid={`title-${i}`}>{t}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Weakness flags */}
                {scoreData.semantic?.weakness_flags?.length > 0 && (
                  <div data-testid="weakness-flags">
                    <p className="text-white text-sm font-bold mb-2 flex items-center gap-2"><AlertTriangle size={14} className="text-amber-400" /> Why you might be getting passed over</p>
                    <div className="space-y-2">
                      {scoreData.semantic.weakness_flags.map((w, i) => (
                        <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3.5" data-testid={`flag-${i}`}>
                          <div className="flex items-start gap-3">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-bold ${SEV_COLOR[w.severity] || SEV_COLOR.Medium}`}>{w.severity}</span>
                            <div className="flex-1 space-y-1">
                              <p className="text-white text-sm font-semibold">{w.issue}</p>
                              <p className="text-zinc-400 text-xs"><span className="text-zinc-500">Why it hurts:</span> {w.why_it_hurts}</p>
                              <p className="text-cyan-300 text-xs"><span className="text-zinc-500">Fix:</span> {w.fix}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* JD match */}
                {scoreData.jd_provided && scoreData.semantic?.jd_match && (
                  <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-4 space-y-3" data-testid="jd-match">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-white text-sm font-bold flex items-center gap-2"><Target size={14} className="text-cyan-400" /> Job-description match</p>
                      <div className="flex items-center gap-3 text-xs">
                        <div><span className="text-zinc-500">Keyword match:</span> <span className="text-white font-bold" data-testid="kw-match">{scoreData.semantic.jd_match.keyword_match_pct}%</span></div>
                        <div><span className="text-zinc-500">Callback probability:</span> <span className="text-white font-bold" data-testid="callback-prob">{scoreData.semantic.jd_match.callback_probability}%</span></div>
                      </div>
                    </div>
                    <p className="text-zinc-300 text-xs">{scoreData.semantic.jd_match.verdict}</p>
                    <div className="grid sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-emerald-300 text-[10px] uppercase tracking-wider font-bold mb-1">Matched keywords</p>
                        <div className="flex flex-wrap gap-1">
                          {(scoreData.semantic.jd_match.matched_keywords || []).map((k, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[10px]">{k}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-red-300 text-[10px] uppercase tracking-wider font-bold mb-1">Missing keywords (add these)</p>
                        <div className="flex flex-wrap gap-1">
                          {(scoreData.semantic.jd_match.missing_keywords || []).map((k, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-200 text-[10px]" data-testid={`missing-kw-${i}`}>{k}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {scoreData.semantic.jd_match.skill_gaps?.length > 0 && (
                      <div>
                        <p className="text-amber-300 text-[10px] uppercase tracking-wider font-bold mb-1">Skill gaps</p>
                        <ul className="text-zinc-300 text-xs space-y-0.5 list-disc list-inside">
                          {scoreData.semantic.jd_match.skill_gaps.map((g, i) => <li key={i}>{g}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Phase 3 — Skill-gap → Python Lab cross-sell */}
                {(skillGapLoading || (skillGapRecs && skillGapRecs.recommended_modules?.length > 0)) && (
                  <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-zinc-900/40 to-zinc-900/40 p-4 space-y-3"
                       data-testid="skill-gap-recommendations">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-500/20 blur-3xl rounded-full pointer-events-none" />
                    <div className="flex items-start justify-between gap-3 flex-wrap relative">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
                          <Code2 size={16} className="text-cyan-300" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">Close your gaps on Python Lab</p>
                          <p className="text-zinc-400 text-[11px]">Targeted modules to unblock callbacks</p>
                        </div>
                      </div>
                      <Link to="/ai-tools/python-lab"
                            className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                            data-testid="pylab-roadmap-link">
                        Full roadmap →
                      </Link>
                    </div>

                    {skillGapLoading && (
                      <div className="flex items-center gap-2 text-zinc-400 text-xs relative">
                        <Loader2 size={14} className="animate-spin" /> Matching gaps to Python Lab modules…
                      </div>
                    )}

                    {!skillGapLoading && skillGapRecs?.recommended_modules?.length > 0 && (
                      <>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 relative">
                          {skillGapRecs.recommended_modules.map((m, i) => (
                            <Link key={m.module_slug}
                                  to={m.deeplink}
                                  className="group bg-zinc-950/60 hover:bg-zinc-900/80 border border-zinc-800 hover:border-cyan-500/50 rounded-xl p-3 transition-all hover:-translate-y-0.5"
                                  data-testid={`pylab-rec-${i}`}>
                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                <p className="text-white text-[13px] font-bold leading-snug group-hover:text-cyan-200 transition-colors">
                                  {m.module_title}
                                </p>
                                <Rocket size={13} className="text-zinc-600 group-hover:text-cyan-300 mt-0.5 shrink-0 transition-colors" />
                              </div>
                              <p className="text-zinc-400 text-[11px] leading-relaxed mb-2">
                                {m.why_it_closes_gap}
                              </p>
                              {m.matched_signals?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {m.matched_signals.slice(0, 3).map((s) => (
                                    <span key={s} className="px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-[10px]">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-1 border-t border-zinc-800/60">
                                <span>{m.lessons_count} lessons</span>
                                <span>~{Math.round(m.est_minutes / 60)}h</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                        {skillGapRecs.cta_message && (
                          <p className="text-cyan-100 text-xs relative leading-relaxed" data-testid="pylab-cta-message">
                            {skillGapRecs.cta_message}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Heuristic breakdown */}
                <details className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-4" data-testid="heuristic-details">
                  <summary className="cursor-pointer text-white text-sm font-bold flex items-center gap-2"><ClipboardList size={14} /> Detailed heuristic breakdown <ChevronDown size={13} className="ml-auto" /></summary>
                  <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    {Object.entries(scoreData.heuristic.components).map(([k, v]) => (
                      <div key={k} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-zinc-400 uppercase tracking-wider text-[10px]">{k.replace(/_/g, " ")}</span>
                          <span className={`font-bold ${v.score >= 80 ? "text-emerald-300" : v.score >= 60 ? "text-amber-300" : "text-red-300"}`}>{v.score}</span>
                        </div>
                        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${v.score}%`, background: v.score >= 80 ? "#86EFAC" : v.score >= 60 ? "#FCD34D" : "#FB7185" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {scoreData.heuristic.heuristic_flags?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-zinc-400 text-xs">
                      {scoreData.heuristic.heuristic_flags.map((f, i) => (
                        <li key={i} className="flex items-start gap-2"><XCircle size={12} className="text-red-400 mt-0.5 shrink-0" /> {f}</li>
                      ))}
                    </ul>
                  )}
                </details>

                {/* India-specific notes */}
                {scoreData.semantic?.india_specific_notes?.length > 0 && (
                  <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3" data-testid="india-notes">
                    <p className="text-orange-300 text-[11px] uppercase tracking-wider font-bold mb-1">India-specific tips</p>
                    <ul className="text-zinc-300 text-xs space-y-0.5 list-disc list-inside">
                      {scoreData.semantic.india_specific_notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  </div>
                )}

                <div className="pt-2">
                  <button onClick={() => setActiveOutput("rewrite")}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-black font-bold text-sm"
                          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                          data-testid="go-rewrite-btn">
                    <Sparkles size={14} /> See ATS-safe rewrite
                  </button>
                </div>
              </div>
            )}

            {activeOutput === "rewrite" && (
              <div className="space-y-4" data-testid="output-rewrite">
                {rewriteLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Crafting your ATS-safe rewrite…</p>}
                {rewriteErr && <p className="text-amber-400 text-xs" data-testid="rewrite-error">{rewriteErr}</p>}
                {rewrite && (
                  <>
                    <div className="flex justify-end gap-2 flex-wrap">
                      {!editingRewrite ? (
                        <>
                          <button onClick={startEdit}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700"
                                  data-testid="edit-rewrite-btn">
                            <Edit3 size={12} /> Edit
                          </button>
                          <CopyBtn text={rewrite.rewritten_markdown} label="Copy Markdown" testid="rewrite-copy" />
                          <button onClick={downloadPdf} disabled={pdfLoading}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] text-black font-bold disabled:opacity-50"
                                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                                  data-testid="download-pdf-btn">
                            {pdfLoading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={cancelEdit}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700"
                                  data-testid="cancel-edit-btn">
                            <X size={12} /> Cancel
                          </button>
                          <button onClick={applyEdit}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] text-black font-semibold"
                                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                                  data-testid="confirm-edit-btn">
                            <Check size={12} /> Apply
                          </button>
                        </>
                      )}
                    </div>
                    {editingRewrite ? (
                      <div className="grid lg:grid-cols-2 gap-4">
                        <div>
                          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Edit3 size={11} /> Markdown source</p>
                          <textarea value={editBuffer} onChange={(e) => setEditBuffer(e.target.value)}
                                    rows={24}
                                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-4 py-3 text-xs text-white placeholder-zinc-600 outline-none font-mono leading-relaxed"
                                    data-testid="rewrite-edit-textarea" />
                        </div>
                        <div>
                          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye size={11} /> Live preview</p>
                          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 prose prose-invert prose-h1:text-2xl prose-h2:text-base prose-h2:text-cyan-300 prose-headings:text-white prose-p:text-zinc-300 prose-li:text-zinc-300 prose-strong:text-white max-w-none max-h-[540px] overflow-y-auto">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{editBuffer}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-6 prose prose-invert prose-h1:text-2xl prose-h1:font-bold prose-h1:mb-2 prose-h1:pb-2 prose-h1:border-b prose-h1:border-zinc-800 prose-h2:text-base prose-h2:font-bold prose-h2:mt-6 prose-h2:mb-2 prose-h2:text-cyan-300 prose-h2:uppercase prose-h2:tracking-wider prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-3 prose-headings:text-white prose-p:text-zinc-300 prose-p:leading-relaxed prose-li:text-zinc-300 prose-li:my-0.5 prose-strong:text-white prose-ul:my-2 max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{rewrite.rewritten_markdown}</ReactMarkdown>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeOutput === "cover" && (
              <div className="space-y-4" data-testid="output-cover">
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 grid sm:grid-cols-3 gap-3">
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><Building2 size={11} /> Company</p>
                    <input type="text" value={coverCompany} onChange={(e) => setCoverCompany(e.target.value)}
                           placeholder="e.g. Acme"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="cover-company-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><Briefcase size={11} /> Role</p>
                    <input type="text" value={coverRole} onChange={(e) => setCoverRole(e.target.value)}
                           placeholder="e.g. Senior Backend Engineer"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="cover-role-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><User2 size={11} /> Hiring manager (optional)</p>
                    <input type="text" value={coverManager} onChange={(e) => setCoverManager(e.target.value)}
                           placeholder="e.g. Priya Sharma"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="cover-manager-input" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={generateCover} disabled={coverLoading}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                          data-testid="generate-cover-btn">
                    {coverLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {cover ? "Regenerate" : "Generate cover letter"}
                  </button>
                  <p className="text-zinc-500 text-[11px]">~12-20 seconds · Needs a job description on the input step</p>
                </div>
                {coverErr && <p className="text-amber-400 text-xs" data-testid="cover-error">{coverErr}</p>}
                {cover && (
                  <>
                    <div className="flex justify-end gap-2">
                      <span className="text-zinc-500 text-[11px] mr-auto">{cover.word_count} words</span>
                      <CopyBtn text={cover.cover_letter_markdown} label="Copy" testid="cover-copy" />
                    </div>
                    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-6 prose prose-invert prose-headings:text-white prose-p:text-zinc-300 prose-p:leading-relaxed prose-strong:text-white max-w-none" data-testid="cover-preview">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cover.cover_letter_markdown}</ReactMarkdown>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeOutput === "linkedin" && (
              <div className="space-y-4" data-testid="output-linkedin">
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1">Current headline</p>
                    <input type="text" value={liHeadline} onChange={(e) => setLiHeadline(e.target.value)}
                           placeholder="e.g. Software Engineer at Acme"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="li-headline-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1">Current About section</p>
                    <textarea value={liAbout} onChange={(e) => setLiAbout(e.target.value)} rows={5}
                              placeholder="Paste your current LinkedIn About section…"
                              className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                              data-testid="li-about-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1">One experience entry (current/recent role)</p>
                    <textarea value={liExperience} onChange={(e) => setLiExperience(e.target.value)} rows={4}
                              placeholder="Paste one experience entry (title, company, bullets)…"
                              className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                              data-testid="li-experience-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1">Skills (comma-separated)</p>
                    <textarea value={liSkills} onChange={(e) => setLiSkills(e.target.value)} rows={2}
                              placeholder="e.g. Python, FastAPI, React, AWS, …"
                              className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                              data-testid="li-skills-input" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={generateLinkedIn} disabled={linkedinLoading}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                          data-testid="generate-linkedin-btn">
                    {linkedinLoading ? <Loader2 size={13} className="animate-spin" /> : <Linkedin size={13} />}
                    {linkedin ? "Re-analyze" : "Analyze & optimize"}
                  </button>
                  <p className="text-zinc-500 text-[11px]">~15-30 seconds</p>
                </div>
                {linkedinErr && <p className="text-amber-400 text-xs" data-testid="linkedin-error">{linkedinErr}</p>}
                {linkedin && (
                  <div className="space-y-3" data-testid="linkedin-result">
                    <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-cyan-500/30 rounded-2xl p-5 flex items-center gap-5">
                      <ScoreDial score={linkedin.overall_score} band={linkedin.overall_score >= 85 ? "Strong" : linkedin.overall_score >= 70 ? "Decent" : linkedin.overall_score >= 50 ? "Needs work" : "Weak"} />
                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold mb-1">LinkedIn profile</p>
                        <p className="text-white text-sm font-semibold">Overall optimization score</p>
                        {linkedin.power_tips?.length > 0 && (
                          <ul className="mt-2 space-y-1 text-zinc-300 text-xs list-disc list-inside">
                            {linkedin.power_tips.slice(0, 4).map((t, i) => <li key={i} data-testid={`li-tip-${i}`}>{t}</li>)}
                          </ul>
                        )}
                      </div>
                    </div>
                    {["headline", "about", "experience"].map((sec) => {
                      const s = linkedin[sec];
                      if (!s) return null;
                      return (
                        <div key={sec} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid={`li-section-${sec}`}>
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <p className="text-white text-sm font-bold capitalize">{sec}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-bold ${s.score >= 80 ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : s.score >= 60 ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-red-500/15 border-red-500/40 text-red-300"}`}>{s.score}/100</span>
                          </div>
                          {s.issues?.length > 0 && (
                            <ul className="text-zinc-400 text-xs space-y-0.5 list-disc list-inside mb-2">
                              {s.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                            </ul>
                          )}
                          <p className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-1">Rewritten</p>
                          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-zinc-200 text-xs whitespace-pre-wrap" data-testid={`li-rewritten-${sec}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.rewritten}</ReactMarkdown>
                          </div>
                          <div className="flex justify-end mt-2">
                            <CopyBtn text={s.rewritten} label="Copy" testid={`copy-li-${sec}`} />
                          </div>
                        </div>
                      );
                    })}
                    {linkedin.skills && (
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid="li-skills-result">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <p className="text-white text-sm font-bold">Skills</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-bold ${linkedin.skills.score >= 80 ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : linkedin.skills.score >= 60 ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-red-500/15 border-red-500/40 text-red-300"}`}>{linkedin.skills.score}/100</span>
                        </div>
                        {linkedin.skills.suggested_top_3?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-emerald-300 text-[10px] uppercase tracking-wider font-bold mb-1">Pin these top 3</p>
                            <div className="flex flex-wrap gap-1">
                              {linkedin.skills.suggested_top_3.map((k, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px]">{k}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {linkedin.skills.add?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-cyan-300 text-[10px] uppercase tracking-wider font-bold mb-1">Add</p>
                            <div className="flex flex-wrap gap-1">
                              {linkedin.skills.add.map((k, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-[11px]">+ {k}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {linkedin.skills.remove?.length > 0 && (
                          <div>
                            <p className="text-red-300 text-[10px] uppercase tracking-wider font-bold mb-1">Drop these (weak/generic)</p>
                            <div className="flex flex-wrap gap-1">
                              {linkedin.skills.remove.map((k, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 text-[11px] line-through">− {k}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeOutput === "dms" && (
              <div className="space-y-4" data-testid="output-dms">
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><Building2 size={11} /> Company</p>
                    <input type="text" value={dmCompany} onChange={(e) => setDmCompany(e.target.value)}
                           placeholder="e.g. Acme"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="dm-company-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><Briefcase size={11} /> Role</p>
                    <input type="text" value={dmRole} onChange={(e) => setDmRole(e.target.value)}
                           placeholder="e.g. Senior Backend"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="dm-role-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><User2 size={11} /> Recruiter name</p>
                    <input type="text" value={dmRecruiter} onChange={(e) => setDmRecruiter(e.target.value)}
                           placeholder="e.g. Anjali"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="dm-recruiter-input" />
                  </label>
                  <label className="block">
                    <p className="text-zinc-400 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1.5"><User2 size={11} /> Mutual connection</p>
                    <input type="text" value={dmMutual} onChange={(e) => setDmMutual(e.target.value)}
                           placeholder="e.g. Rahul (ex-Acme)"
                           className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none"
                           data-testid="dm-mutual-input" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={generateDms} disabled={dmsLoading}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                          data-testid="generate-dms-btn">
                    {dmsLoading ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />}
                    {dms ? "Regenerate" : "Generate 3 outreach variants"}
                  </button>
                  <p className="text-zinc-500 text-[11px]">~15-25 seconds</p>
                </div>
                {dmsErr && <p className="text-amber-400 text-xs" data-testid="dms-error">{dmsErr}</p>}
                {dms?.variants && (
                  <div className="space-y-3">
                    {dms.variants.map((v, i) => (
                      <div key={v.id || i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid={`dm-variant-${v.id || i}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <div>
                            <p className="text-white text-sm font-bold">{v.label}</p>
                            <p className="text-zinc-500 text-[11px]">{v.format} · {v.length_words} words · {v.when_to_use}</p>
                          </div>
                          <CopyBtn text={`${v.subject ? "Subject: " + v.subject + "\n\n" : ""}${v.body}`} label="Copy" testid={`copy-dm-${v.id || i}`} />
                        </div>
                        {v.subject && (
                          <p className="text-cyan-300 text-xs mb-2"><span className="text-zinc-500">Subject:</span> {v.subject}</p>
                        )}
                        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-zinc-200 text-xs prose prose-invert prose-p:text-zinc-300 prose-strong:text-white max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{v.body}</ReactMarkdown>
                        </div>
                      </div>
                    ))}
                    {dms.tips?.length > 0 && (
                      <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-3" data-testid="dm-tips">
                        <p className="text-cyan-300 text-[11px] uppercase tracking-wider font-bold mb-1">Outreach tips</p>
                        <ul className="text-zinc-300 text-xs space-y-0.5 list-disc list-inside">
                          {dms.tips.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
